import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ReviewFileApi } from 'api';
import type { ReviewFile } from 'api/methods/ReviewFileApi';

/**
 * [ReviewFile] Query key 생성
 * --
 */
export const ReviewFileQueryKeys = createQueryKeys('reviewFile', {
  getReviewFile: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getReviewFiles: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [ReviewFile] 후기 전체 조회
 * --
 */
export const useGetReviewFiles = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: ReviewFileQueryKeys.getReviewFiles(params).queryKey,
    queryFn: () => ReviewFileApi.getReviewFiles(params),
    ...options,
  });
  return data;
};

/**
 * [ReviewFile] 후기 상세 조회
 * --
 */
export const useGetReviewFile = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: ReviewFileQueryKeys.getReviewFile(idx, params).queryKey,
    queryFn: () => ReviewFileApi.getReviewFile(idx, params),
    ...options,
  });

  return data;
};

/**
 * [ReviewFile] 후기 생성
 * --
 */
export const useCreateReviewFile = () => {
  const { mutateAsync: createReviewFile, ...result } = useMutation({
    mutationFn: (variables: ReviewFile) =>
      ReviewFileApi.createReviewFile(variables),
  });

  return { createReviewFile, ...result };
};

/**
 * [ReviewFile] 후기 수정
 * --
 */
export const useUpdateReviewFile = (body: ReviewFile) => {
  const { mutateAsync: updateReviewFile, ...result } = useMutation({
    mutationFn: (variables: ReviewFile) =>
      ReviewFileApi.updateReviewFile(variables),
  });

  return { updateReviewFile, ...result };
};

/**
 * [ReviewFile] 후기 삭제
 * --
 */
export const useDeleteReviewFile = (idx: number) => {
  const { mutateAsync: deleteReviewFile, ...result } = useMutation({
    mutationFn: (variables: number) =>
      ReviewFileApi.deleteReviewFile(variables),
  });

  return { deleteReviewFile, ...result };
};

/**
 * [ReviewFile] 리뷰 파일 Bulk 생성
 * --
 */
export const useBulkCreateReviewFile = () => {
  const { mutateAsync: bulkCreateReviewFile, ...result } = useMutation({
    mutationFn: (variables: ReviewFile[]) =>
      ReviewFileApi.bulkCreateReviewFile(variables),
  });

  return { bulkCreateReviewFile, ...result };
};
